<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="isVisible => emit('update:isVisible', isVisible)" size="large" :isClosable="false" :isCloseButtonVisible="!isSaving">
    <template #content>
      <div
        :style="{
          opacity: isSaving ? '0.5' : '1',
          pointerEvents: isSaving ? 'none' : 'auto',
        }"
      >
        <div style="margin-bottom: 12px">
          <div v-if="isCreateMode" class="create-title-row">
            <h1 class="global-h1 create-title-row__heading">Create a</h1>
            <SrpDropdown v-if="allPostTypesEnabled" :placement="'bottom-start'" :offset="4">
              <template #clickableElement="{ isDropdownVisible }">
                <h1 class="global-h1 create-title-row__heading create-title-row__heading--clickable" :class="{ 'create-title-row__heading--open': isDropdownVisible }">
                  <!--<IconEmbedded :name="getPostTypeIcon(postType)" :size="getPostTypeIconSize(postType)" color="rgba(0, 0, 0, 0.5)" class="create-title-row__heading-type-icon" />-->
                  {{ postType }}
                  <IconEmbedded :name="isDropdownVisible ? 'caret-top_3' : 'caret-bottom_3'" :size="14" color="gray" class="create-title-row__caret" />
                </h1>
              </template>

              <template #dropdown="{ close }">
                <div class="create-title-row__dropdown">
                  <div
                    v-for="type in postTypeOptions"
                    :key="type"
                    class="create-title-row__option"
                    :class="{ 'create-title-row__option--selected': type === postType }"
                    @click="selectPostType(type, close)"
                  >
                    <div class="create-title-row__option-label">
                      <IconEmbedded :name="getPostTypeIcon(type)" :size="getPostTypeIconSize(type)" color="rgba(0, 0, 0, 0.5)" class="create-title-row__option-icon" />
                      <span class="create-title-row__option-text">{{ type }}</span>
                    </div>
                    <IconEmbedded v-if="type === postType" name="check_3" :size="16" color="rgba(0, 128, 96, 1)" />
                  </div>
                </div>
              </template>
            </SrpDropdown>
            <h1 v-else class="global-h1 create-title-row__heading">Post</h1>
          </div>
          <h1 v-else class="global-h1" style="margin-bottom: 5px">Edit Post Guidance</h1>
        </div>

        <div style="padding-bottom: 10px; border-bottom: 1px solid #e0e0e0">
          <div style="display: flex; gap: 12px; align-items: flex-end; justify-content: space-between; flex-wrap: wrap">
            <SrpSegmentedControl v-model="mode" :options="modeOptions" size="md" aria-label="Post creation mode" />
          </div>
          <p style="color: #666; font-size: 14px; line-height: 1.5; margin-top: 8px">
            <span v-if="isStoryPost">Temporary full-screen posts that let you share photos or videos.</span>
            <span v-else-if="isReelPost">Short-form videos designed for discovery and engagement.</span>
            <span v-else>
              <span v-if="mode === 'ai'">Atlas will create a post based on your guidance</span>
              <span v-else>Set the post details manually yourself</span>
            </span>
          </p>
        </div>

        <div style="min-height: 300px">
          <!-- AI Assisted Mode -->
          <div v-if="mode === 'ai'">
            <div style="margin-top: 20px">
              <label class="input-label">Theme/Purpose</label>
              <input
                :class="{
                  'global-text-input': true,
                  'global-text-input--error': !isFormValid && !_title,
                }"
                v-model="_title"
                style="max-width: 300px"
                placeholder="e.g. festival, local food..."
                type="text"
                @focus="isFormValid = true"
              />
            </div>

            <div style="margin-top: 16px">
              <label class="input-label">Guidance for the AI</label>
              <textarea
                :class="{
                  'global-textarea': true,
                  'global-textarea--error': !isFormValid && !_description,
                }"
                v-model="_description"
                style="height: 85px; min-height: 85px"
                placeholder="Describe the tone, style, key points to include..."
                @focus="isFormValid = true"
              ></textarea>
            </div>

            <input v-if="props.itemToEdit && _specificDate" class="global-text-input" v-model="_specificDate" style="max-width: 300px; margin-top: 12px" placeholder="Timing (optional)" type="text" />

            <div style="margin-top: 20px">
              <h4 style="margin-bottom: 8px">Content Album</h4>
              <div v-if="!selectedContentSummary">
                We'll find a content album to match with, or<br />
                <LinkWithIcon isDottedUnderline @click="isPickContentModalVisible = true" style="margin-top: 8px">
                  <template #icon>
                    <IconEmbedded name="folder_2" :size="19" />
                  </template>
                  <span>Manually choose one</span>
                </LinkWithIcon>
              </div>
              <div v-else style="margin-top: 8px">
                <ContentSummary
                  :contentSummaryData="selectedContentSummary"
                  :isUnbindButtonVisible="false"
                  :isContentSourcedFromLabelVisible="false"
                  :hideDecorations="true"
                  :customerId="props.customerId"
                  :isRemoveButtonVisible="true"
                  @clickViewPhotos="handleViewPhotos"
                  @clickRemove="selectedContentSummary = null"
                />
              </div>
            </div>
          </div>

          <!-- Manual Mode -->
          <div v-if="mode === 'manual'">
            <div v-if="!isStoryPost" style="margin-top: 20px">
              <label class="input-label">Caption</label>
              <textarea
                :class="{
                  'global-textarea': true,
                  'global-textarea--error': !isFormValid && isCaptionRequired && !_caption,
                }"
                v-model="_caption"
                :maxlength="MAX_CAPTION_LENGTH"
                style="height: 85px; min-height: 85px"
                placeholder="Write your post caption..."
                @focus="isFormValid = true"
              ></textarea>
            </div>

            <div style="margin-top: 20px">
              <label class="input-label">{{ manualMediaLabel }}</label>

              <div
                :class="{
                  'photo-section-container': true,
                  'photo-section-container--error': !isFormValid && manualSelectedAssetIds.length === 0,
                }"
                @click="isFormValid = true"
              >
                <!-- Photo action buttons -->
                <div class="photo-actions">
                  <LinkWithIcon isDottedUnderline @click="isManualAlbumPickerOpen = true">
                    <template #icon><IconEmbedded name="folder_2" :size="19" /></template>
                    <span>{{ manualAlbumPayload ? "Change Album" : "Select from an Album" }}</span>
                  </LinkWithIcon>
                  <span class="photo-actions__separator"> </span>
                  <LinkWithIcon isDottedUnderline @click="isUploadSectionOpen = !isUploadSectionOpen">
                    <template #icon><IconEmbedded name="upload_2" :size="17" /></template>
                    <span>{{ uploadMediaLabel }}</span>
                  </LinkWithIcon>
                </div>

                <!-- Upload section -->
                <PhotoUploadSection
                  v-if="isUploadSectionOpen"
                  inputId="new-post-manual-upload"
                  v-model="uploadedAssetIds"
                  :selectedAssetIds="manualSelectedAssetIds"
                  :isVideoUploadAllowed="isVideoUploadAllowedForType"
                  :isVideoOnlyUploadAllowed="isVideoOnlyUploadAllowedForType"
                  @select="handlePhotoUploadSelect"
                  @deselect="handlePhotoUploadDeselect"
                  @close="isUploadSectionOpen = false"
                />

                <!-- Album photos grid -->
                <div v-if="isLoadingManualAlbum" class="thumbnails-list thumbnails-list--skeleton">
                  <div v-for="i in 12" :key="i" class="thumbnail thumbnail--skeleton"></div>
                </div>

                <div v-else-if="manualAlbumPayload">
                  <ul class="thumbnails-list">
                    <SrpFileThumbnail
                      class="thumbnail"
                      v-for="assetId in displayedManualAlbumAssetIds"
                      :key="assetId"
                      :fileName="assetId"
                      galleryPostfix="manualAlbumSelection"
                      :isDeleteButton="false"
                      isEntireAreaCheckable
                    >
                      <input
                        type="checkbox"
                        :value="assetId"
                        :checked="manualSelectedAssetIds.includes(assetId)"
                        :disabled="isAtPhotoLimit && !manualSelectedAssetIds.includes(assetId)"
                        @change="$event => selectOrDeselectPhoto(($event.target as HTMLInputElement).checked, assetId)"
                      />
                    </SrpFileThumbnail>
                  </ul>
                </div>

                <!-- Sticky panel with warning and selected files -->
                <div class="sticky-panel">
                  <!-- Limit warning -->
                  <PhotoLimitWarning :isAtLimit="isAtPhotoLimit" />

                  <!-- Selected files panel -->
                  <SelectedFilesPanel
                    v-if="manualSelectedAssetIds.length"
                    class="selected-files-panel"
                    :selectedFiles="manualSelectedAssetIds.map(id => ({ serverId: id })) as UploadedImage[]"
                    @remove="assetId => selectOrDeselectPhoto(false, assetId)"
                    @reorder="newArray => (manualSelectedAssetIds = newArray.map(({ serverId }) => serverId))"
                    :isPreviewLinkVisible="false"
                  />
                </div>
              </div>

              <div v-if="!isFormValid && manualValidationErrorMessage" class="global-error-string" style="margin-top: 8px">{{ manualValidationErrorMessage }}</div>
            </div>
          </div>
        </div>

        <div class="new-post-modal__superuser-actions">
          <SuperUserActionPanel>
            <SrpButton size="tiny" :fill="allPostTypesEnabled ? 'solid' : 'outlined'" color="gray" @click="allPostTypesEnabled = !allPostTypesEnabled">All Types</SrpButton>
          </SuperUserActionPanel>
        </div>
      </div>
    </template>

    <template #footer="{ closeModal }">
      <div style="gap: 12px; display: flex">
        <SrpButton
          fill="outlined"
          color="gray"
          @click="
            () => {
              emit('cancel');
              closeModal();
            }
          "
          :isDisabled="isSaving"
        >
          Cancel
        </SrpButton>

        <SrpButton
          @click="
            async () => {
              const success = await save();
              if (success) {
                closeModal();
              }
            }
          "
          :isDisabled="isSaving"
        >
          <template #icon>
            <LoadingIcon v-if="isSaving" :size="20" />
            <IconEmbedded v-else :name="mode === 'manual' || !isCreateMode ? 'save_2-5' : 'ai_2'" :size="20" />
          </template>
          {{ buttonText }}
        </SrpButton>
      </div>
    </template>
  </SrpModal>

  <PickFromExistingContentModal
    v-if="isPickContentModalVisible"
    v-model:isVisible="isPickContentModalVisible"
    :customerId="props.customerId"
    :calendarItem="null"
    @close="isPickContentModalVisible = false"
    @picked="contentSummary => handleContentSummarySelected(contentSummary)"
  />

  <ViewAllPhotosModal v-if="isViewAllPhotosModalVisible" v-model:isVisible="isViewAllPhotosModalVisible" :customerId="props.customerId" :contentSummaryId="viewAllPhotosSummaryId" />

  <PickFromExistingContentModal
    v-if="isManualAlbumPickerOpen"
    v-model:isVisible="isManualAlbumPickerOpen"
    :customerId="props.customerId"
    :calendarItem="null"
    @close="isManualAlbumPickerOpen = false"
    @picked="handleManualAlbumSelected"
  />
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch } from "vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import { useToastsStore } from "@stores/toasts";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpSegmentedControl from "@components/ui/SrpSegmentedControl.vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import PickFromExistingContentModal from "@views/Community/ContentCalendar/steps/modals/PickFromExistingContentModal.vue";
import ContentSummary from "@views/Community/ContentCalendar/eventSnippets/ContentSummary.vue";
import ViewAllPhotosModal from "@views/Community/ContentCalendar/steps/modals/ViewAllPhotosModal.vue";
import SelectedFilesPanel from "@components/SelectedFilesPanel.vue";
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import PhotoUploadSection from "@components/SocialPostEditAndShareModal/PhotoUploadSection.vue";
import PhotoLimitWarning from "@components/SocialPostEditAndShareModal/PhotoLimitWarning.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";

// Types
import { ContentCalendarItem, ContentCalendarItemMatch, ContentSummaryPreview, SocialPostType } from "@contracts/contentCalendar";
import type { IconName } from "@contracts/IconName";
import { UploadedImage } from "@contracts/uploadedImage";
import { SocialShareSummaryPayload, SocialShareSummaryContentType } from "@contracts/shareStop";

// Repos
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";

// Constants
import { MAX_PHOTOS_PER_POST, MAX_CAPTION_LENGTH } from "@/constants/social";
import { getGlobalRemoteLogger } from "@/helpers/RemoteLogger";
import FileUtils from "@logic/FileUtils";

/** When true, manual Post and Story allow photos and videos; Reels always use video only. */
const allowVideoForManualPosts = false;

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    itemToEdit?: ContentCalendarItem;
    customerId: string;
    weekGroup?: string | null;
    subTitle?: string;
  }>(),
  {
    itemToEdit: null,
    weekGroup: null,
    subTitle: "",
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "save", item: ContentCalendarItem): void;
  (e: "createCalendarItem", item: ContentCalendarItem): void;
  (e: "createNewItemAndPost", newItemAndPost: ContentCalendarItemMatch): void;
  (e: "cancel"): void;
}>();

// Local values ===============================================================
const _title = ref<string>("");
const _description = ref<string>("");
const _specificDate = ref<string>("");

// Mode toggle ================================================================
const mode = ref<string>("ai");
/** Super-user panel: when true, Post / Reel / Story dropdown is shown; when false, title is "Create a Post" only. */
const allPostTypesEnabled = ref(false);
const postType = ref<SocialPostType>(SocialPostType.Post);
const postTypeOptions: SocialPostType[] = [SocialPostType.Post, SocialPostType.Reel, SocialPostType.Story];
const postTypeIconMap: Record<SocialPostType, IconName> = {
  [SocialPostType.Post]: "social-post",
  [SocialPostType.Reel]: "social-reel",
  [SocialPostType.Story]: "social-story",
};
const modeOptions = computed(() => [
  { value: "ai", label: "AI Assisted", icon: "ai_2" as const, disabled: postType.value !== SocialPostType.Post },
  { value: "manual", label: "Manual Mode", icon: "edit-pencil_2" as const },
]);

function getPostTypeIcon(type: SocialPostType): IconName {
  return postTypeIconMap[type];
}

function getPostTypeIconSize(type: SocialPostType): number {
  return type === SocialPostType.Post ? 18 : 16;
}

function selectPostType(type: SocialPostType, closeDropdown: () => void) {
  postType.value = type;
  closeDropdown();
}

const isStoryPost = computed(() => postType.value === SocialPostType.Story);
const isReelPost = computed(() => postType.value === SocialPostType.Reel);

const manualAlbumContentType = computed(() => {
  if (postType.value === SocialPostType.Reel) {
    return SocialShareSummaryContentType.Videos;
  }
  if (allowVideoForManualPosts) {
    return SocialShareSummaryContentType.All;
  }
  return SocialShareSummaryContentType.Photos;
});
const isCaptionRequired = computed(() => postType.value !== SocialPostType.Story);
const isVideoUploadAllowedForType = computed(() => postType.value === SocialPostType.Reel || allowVideoForManualPosts);
const isVideoOnlyUploadAllowedForType = computed(() => postType.value === SocialPostType.Reel);
const manualMediaLabel = computed(() => (isVideoOnlyUploadAllowedForType.value ? "Videos" : allowVideoForManualPosts ? "Photos and Videos" : "Photos"));
const uploadMediaLabel = computed(() => (isVideoOnlyUploadAllowedForType.value ? "Upload Videos" : allowVideoForManualPosts ? "Upload Photos and Videos" : "Upload Photos"));
const manualValidationErrorMessage = computed(() => {
  if (isFormValid.value || mode.value !== "manual") {
    return "";
  }
  if (manualSelectedAssetIds.value.length === 0) {
    if (isVideoOnlyUploadAllowedForType.value) {
      return "Please select at least one video";
    }
    return allowVideoForManualPosts ? "Please select at least one photo or video" : "Please select at least one photo";
  }
  if (isCaptionRequired.value && !_caption.value?.trim()) {
    return "Please add a caption";
  }
  return "";
});

watch(allPostTypesEnabled, enabled => {
  if (!enabled) {
    postType.value = SocialPostType.Post;
  }
});

watch(postType, newType => {
  if (newType !== SocialPostType.Post && mode.value === "ai") {
    mode.value = "manual";
  }
  if (newType === SocialPostType.Reel) {
    manualSelectedAssetIds.value = manualSelectedAssetIds.value.filter(assetId => FileUtils.isVideoFileType(assetId));
  } else if (!allowVideoForManualPosts) {
    manualSelectedAssetIds.value = manualSelectedAssetIds.value.filter(assetId => !FileUtils.isVideoFileType(assetId));
  }
  if (manualContentSummaryPreview.value) {
    loadManualAlbumPayload(manualContentSummaryPreview.value);
  }
  isFormValid.value = true;
});

watch(mode, newMode => {
  if (newMode === "ai" && postType.value !== SocialPostType.Post) {
    mode.value = "manual";
  }
});

// Content group selection ====================================================
const selectedContentSummary = ref<ContentSummaryPreview | null>(null);
const isPickContentModalVisible = ref<boolean>(false);

function handleContentSummarySelected(contentSummary: ContentSummaryPreview) {
  selectedContentSummary.value = contentSummary;
  isPickContentModalVisible.value = false;
}

// View all photos modal ======================================================
const isViewAllPhotosModalVisible = ref<boolean>(false);
const viewAllPhotosSummaryId = ref<string>("");

function handleViewPhotos() {
  viewAllPhotosSummaryId.value = selectedContentSummary.value?.id || "";
  isViewAllPhotosModalVisible.value = true;
}

// Manual mode state ===========================================================
const _caption = ref<string>("");
const manualSelectedAssetIds = ref<string[]>([]);
const uploadedAssetIds = ref<string[]>([]);
const isUploadSectionOpen = ref<boolean>(false);
const isManualAlbumPickerOpen = ref<boolean>(false);
const manualAlbumPayload = ref<SocialShareSummaryPayload | null>(null);
const manualContentSummaryPreview = ref<ContentSummaryPreview | null>(null);
const manualUploadsSelectedCount = ref<number>(0);
const isLoadingManualAlbum = ref<boolean>(false);

onMounted(() => {
  _title.value = props.itemToEdit?.title ?? "";
  _description.value = props.itemToEdit?.description ?? "";
});

// Is Create Mode =============================================================
const isCreateMode = computed<boolean>(() => !props.itemToEdit);

// Button text =================================================================
const buttonText = computed<string>(() => {
  if (mode.value === "manual") {
    return isSaving.value ? "Saving" : "Save";
  }
  return isCreateMode.value ? (isSaving.value ? "Generating" : "Generate") : isSaving.value ? "Updating" : "Update";
});

// Validate form ==============================================================
const isFormValid = ref<boolean>(true);

// Manual mode helpers ========================================================
function selectOrDeselectPhoto(isSelected: boolean, assetId: string) {
  if (isSelected) {
    if (!manualSelectedAssetIds.value.includes(assetId)) {
      manualSelectedAssetIds.value.push(assetId);
    }
    // Clear validation error when a photo is selected
    if (manualSelectedAssetIds.value.length > 0) {
      isFormValid.value = true;
    }
  } else {
    manualSelectedAssetIds.value = manualSelectedAssetIds.value.filter(id => id !== assetId);
  }
}

function handlePhotoUploadSelect(assetId: string) {
  manualUploadsSelectedCount.value++;
  selectOrDeselectPhoto(true, assetId);
}

function handlePhotoUploadDeselect(assetId: string) {
  manualUploadsSelectedCount.value--;
  selectOrDeselectPhoto(false, assetId);
}

async function handleManualAlbumSelected(contentSummary: ContentSummaryPreview) {
  manualContentSummaryPreview.value = contentSummary; // Store the preview for later
  isManualAlbumPickerOpen.value = false;
  await loadManualAlbumPayload(contentSummary);
}

async function loadManualAlbumPayload(contentSummary: ContentSummaryPreview) {
  isLoadingManualAlbum.value = true;
  try {
    manualAlbumPayload.value = await ShareStopSummaryRepo.loadSocialShareSummaryPayloadFromSummaryId(props.customerId, contentSummary.id, manualAlbumContentType.value);
  } finally {
    isLoadingManualAlbum.value = false;
  }
}

const isAtPhotoLimit = computed(() => manualSelectedAssetIds.value.length >= MAX_PHOTOS_PER_POST);

const displayedManualAlbumAssetIds = computed(() => {
  if (!manualAlbumPayload.value) {
    return [];
  }
  const assets = manualAlbumPayload.value.assets;
  if (postType.value === SocialPostType.Reel) {
    return assets.filter(assetId => FileUtils.isVideoFileType(assetId));
  }
  if (!allowVideoForManualPosts) {
    return assets.filter(assetId => !FileUtils.isVideoFileType(assetId));
  }
  return assets;
});

// Save =======================================================================
const isSaving = ref<boolean>(false);

async function save(): Promise<boolean> {
  isSaving.value = true;

  if (mode.value === "manual") {
    // Manual mode validation and save
    if (!validatePostTypeInputs()) {
      isFormValid.value = false;
      isSaving.value = false;
      return false;
    }

    const response = await ContentCalendarRepo.createManualPost(props.customerId, {
      caption: isStoryPost.value ? "" : _caption.value,
      assetIds: manualSelectedAssetIds.value,
      postType: postType.value,
      weekGroup: props.weekGroup,
      contentSourceType: manualContentSummaryPreview.value?.contextType,
      sourceEntityId: manualContentSummaryPreview.value?.contextId,
      manualUploadsSelected: manualUploadsSelectedCount.value,
    });

    // We have both the post and item created immediately
    emit("createNewItemAndPost", response.newItemAndPost);
    isSaving.value = false;
    return true;
  } else if (mode.value === "ai") {
    if (postType.value !== SocialPostType.Post) {
      isFormValid.value = false;
      isSaving.value = false;
      return false;
    }

    // AI mode validation and save
    if (!_title.value || !_description.value) {
      isFormValid.value = false;
      isSaving.value = false;
      return false;
    }

    const response = await ContentCalendarRepo.matchAndGeneratePost(props.customerId, {
      title: _title.value,
      description: _description.value,
      weekGroup: props.weekGroup,
      contentSourceType: selectedContentSummary.value?.contextType,
      sourceEntityId: selectedContentSummary.value?.contextId,
    });

    if (response.failedToMatch) {
      useToastsStore().warn({
        message: "Couldn't find suitable content",
        expiresInMs: 8000,
        details: "The guidance was created but we couldn't match it to any of your existing content.",
      });
    }

    // We just have the item created and the post will generate async
    emit("createCalendarItem", response.calendarItem);
    isSaving.value = false;
    return true;
  } else if (props.itemToEdit) {
    // Edit mode (shouldn't ever show so could retire this?)
    const updatedItem = await ContentCalendarRepo.updateItem(props.customerId, {
      ...props.itemToEdit,
      title: _title.value,
      description: _description.value,
    });

    emit("save", updatedItem);
    isSaving.value = false;
    return true;
  } else {
    getGlobalRemoteLogger().error(`NewPostModal: Unknown mode ${mode.value} for ${props.customerId}`);
    isSaving.value = false;
    return false;
  }
}

function validatePostTypeInputs(): boolean {
  // TODO: Add full post-type-specific validation rules (aspect ratio, duration, codecs, and per-network limits):
  // https://info.bundle.social/api-reference/platforms/instagram or https://www.ayrshare.com/docs/media-guidelines/instagram
  if (manualSelectedAssetIds.value.length === 0) {
    return false;
  }
  if (isCaptionRequired.value && !_caption.value?.trim()) {
    return false;
  }
  return true;
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/mixins/animations/loading-bg.scss";
@import "@/components/SocialPostEditAndShareModal/photo-upload";

.input-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 3px !important;
}

.create-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
}

.create-title-row__heading {
  margin: 0;
  display: inline-flex;
  align-items: center;

  &--clickable {
    cursor: pointer;
    user-select: none;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    transition:
      border-color 0.15s ease,
      background-color 0.15s ease;

    &:hover {
      border-color: rgba(0, 128, 128, 0.3);
      background-color: rgba(0, 128, 128, 0.04);
    }
  }

  &--open {
    border-color: rgba(0, 128, 128, 0.4);
    background-color: rgba(0, 128, 128, 0.06);
  }
}

.create-title-row__caret {
  margin-left: 10px;
  padding-left: 10px;
  border-left: 1px solid rgba(0, 0, 0, 0.15);
}

.create-title-row__heading-type-icon {
  margin-right: 6px;
}

.create-title-row__dropdown {
  min-width: 180px;
  background: white;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.create-title-row__option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  cursor: pointer;
  transition: background-color 0.1s ease;

  &:hover {
    background-color: rgba(0, 128, 128, 0.06);
  }

  &--selected {
    background-color: rgba(0, 128, 128, 0.08);
  }
}

.create-title-row__option-label {
  display: inline-flex;
  align-items: center;
}

.create-title-row__option-icon {
  margin-right: 6px;
}

.create-title-row__option-text {
  font-size: 14px;
  color: #333;
}

// Photo section container ======================================================
.photo-section-container {
  padding: 12px;
  border-radius: 4px;
  transition:
    border-color 0.05s ease-in-out,
    box-shadow 0.05s ease-in-out;

  &--error {
    border: 1px rgba(178, 34, 34, 0.6) solid;
    box-shadow: 0 0 0 5px rgba(178, 34, 34, 0.2);
  }
}

// Photo actions =================================================================
.photo-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;

  &__separator {
    color: rgba(0, 0, 0, 0.5);
    font-size: 13px;
  }
}

// Upload section ================================================================
.upload-section {
  margin-top: 12px;
}

// Thumbnails list ==============================================================
.thumbnails-list {
  width: calc(100% + 15px);
  padding: 0;
  margin: 12px 0 20px;
  display: flex;
  flex-wrap: wrap;
  list-style: none;

  &--skeleton {
    height: 200px;
    align-content: flex-start;
    position: relative;
    overflow: hidden;
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.thumbnail {
  width: 113px;
  margin: 0 11px 11px 0;

  &--skeleton {
    @include loadingBgAnimation;
    height: 85px;
  }
}

// Sticky panel ===================================================================
.sticky-panel {
  background: white;
  position: sticky;
  bottom: -40px;
}

// Selected files panel =========================================================
.selected-files-panel {
  margin-top: 8px;
}

// Super-user panel: hug content, align right =====================================
.new-post-modal__superuser-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
  width: 100%;

  :deep(.super-user-action-panel) {
    width: fit-content;
    max-width: 100%;
  }
}
</style>
